import {
  API_PATHS,
  API_V1_PREFIX,
  JOB_STATUS_LABELS,
  type JobStatus,
} from '@uroute/shared';
import { Button, EmptyState } from '@uroute/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AddressBlock } from '../components/AddressBlock';
import { DeadlineTag } from '../components/DeadlineTag';
import { ExceptionSheet } from '../components/ExceptionSheet';
import { PodSheet } from '../components/PodSheet';
import { ProofThumb } from '../components/ProofThumb';
import { StatusStepper } from '../components/StatusStepper';
import { DetailSkeleton } from '../components/Skeleton';
import { ArrowLeft, Check, Clock, Package, Play, ShieldCheck, AlertTriangle, Weight } from '../components/icons';
import { useDriverActions, useJob, useJobHistory, useJobProofs, jobFrom } from '../lib/jobs';
import { isDelivered } from '../lib/jobStatus';
import { makeItem } from '../lib/queue';
import { formatMoment, formatTime, windowLabel } from '../lib/format';

const STATUS_ACTION: Partial<Record<JobStatus, { to: JobStatus; label: string; testid: string; variant?: 'primary' | 'secondary' }>> = {
  allocated: { to: 'accepted', label: 'Accept job', testid: 'accept-job' },
  accepted: { to: 'in_progress', label: 'Start delivery', testid: 'start-job' },
  in_progress: undefined, // completed via PodSheet, exception via ExceptionSheet
  exception: { to: 'in_progress', label: 'Resume delivery', testid: 'resume-job' },
};

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobQuery = useJob(id);
  const history = useJobHistory(id);
  const proofsQuery = useJobProofs(id);
  const runActions = useDriverActions();

  const [podOpen, setPodOpen] = useState(false);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const job = jobFrom(jobQuery.data);
  const proofs = proofsQuery.proofs;

  if (jobQuery.loading && !job) return <DetailSkeleton />;

  if (!job) {
    return (
      <EmptyState
        icon={<Package size={40} />}
        title={jobQuery.offline ? 'Job not cached offline' : 'Job not found'}
        description={
          jobQuery.offline
            ? 'This job hasn&rsquo;t been opened on this device yet. Reconnect and pull to refresh.'
            : 'It may have been removed or you don&rsquo;t have access.'
        }
        action={
          <Button variant="secondary" onClick={() => navigate('/jobs')} data-testid="back-to-jobs">
            Back to jobs
          </Button>
        }
      />
    );
  }

  const action = STATUS_ACTION[job.status];
  const canComplete = job.status === 'in_progress';
  /* The shared state machine only allows in_progress -> exception for drivers. */
  const canException = job.status === 'in_progress';
  const delivered = isDelivered(job.status);

  const transition = async (to: JobStatus) => {
    setBusy(true);
    const ok = await runActions(
      [
        makeItem({
          kind: 'status',
          label: `${JOB_STATUS_LABELS[to]} ${job.externalRef}`,
          path: `${API_PATHS.jobs}/${job.id}/status`,
          jobId: job.id,
          json: { to },
        }),
      ],
      {
        success: JOB_STATUS_LABELS[to],
        queued: `${JOB_STATUS_LABELS[to]} saved`,
      },
    );
    setBusy(false);
    if (ok) void jobQuery.refresh();
  };

  const acceptAck = async () => {
    setBusy(true);
    /* POST /me/jobs/:id/acknowledge performs allocated -> accepted itself. */
    const ok = await runActions(
      [
        makeItem({
          kind: 'acknowledge',
          label: `Accept ${job.externalRef}`,
          path: `${API_V1_PREFIX}/me/jobs/${job.id}/acknowledge`,
          jobId: job.id,
        }),
      ],
      { success: 'Job accepted', queued: 'Acceptance saved' },
    );
    setBusy(false);
    if (ok) void jobQuery.refresh();
  };

  const timeline = history.entries.slice(0, 8);

  return (
    <article className="detail" data-testid="job-detail" data-job-id={job.id}>
      <header className="detail__head">
        <button
          type="button"
          className="iconbtn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="detail-back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="detail__headbody">
          <span className="detail__ref u-data">{job.externalRef}</span>
          <span className="detail__status">{JOB_STATUS_LABELS[job.status]}</span>
        </div>
        <DeadlineTag job={job} showSub />
      </header>

      <StatusStepper status={job.status} />

      <section className="detail__meta" aria-label="Job summary">
        <span className="chip">
          <Weight size={14} />
          <span className="u-data">{job.weightKg} kg</span>
        </span>
        <span className="chip">
          <Package size={14} />
          <span className="u-data">{job.parcels}</span> parcel{job.parcels === 1 ? '' : 's'}
        </span>
        {job.timeWindowStart || job.timeWindowEnd ? (
          <span className="chip">
            <Clock size={14} />
            {windowLabel(job)}
          </span>
        ) : null}
        {job.podRequired ? (
          <span className="chip chip--pod">
            <ShieldCheck size={14} />
            POD required
          </span>
        ) : null}
      </section>

      <AddressBlock kind="collection" address={job.collectionAddress} />
      <AddressBlock kind="delivery" address={job.deliveryAddress} />

      {job.description ? (
        <section className="detail__notes" data-testid="job-description">
          <h3 className="section-label">Delivery notes</h3>
          <p className="detail__notestext">{job.description}</p>
        </section>
      ) : null}

      {job.handlingInstructions ? (
        <section className="detail__notes detail__notes--handling" data-testid="job-handling">
          <h3 className="section-label">Handling</h3>
          <p className="detail__notestext">{job.handlingInstructions}</p>
        </section>
      ) : null}

      {proofs.length > 0 ? (
        <section className="detail__proofs" data-testid="job-proofs">
          <h3 className="section-label">Proof of delivery</h3>
          <ul className="proofrow">
            {proofs.map((proof) => (
              <li key={proof.id}>
                <ProofThumb proof={proof} size="sm" />
                <span className="proofrow__meta u-data">
                  {proof.type}
                  {proof.recipientName ? ` · ${proof.recipientName}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {timeline.length > 0 ? (
        <section className="detail__timeline" data-testid="job-timeline">
          <h3 className="section-label">History</h3>
          <ol className="timeline">
            {timeline.map((entry) => (
              <li key={entry.id} className="timeline__item">
                <span className="timeline__dot" aria-hidden="true" />
                <span className="timeline__body">
                  <span className="timeline__label">
                    {JOB_STATUS_LABELS[entry.toStatus]}
                    {entry.fromStatus ? ` from ${JOB_STATUS_LABELS[entry.fromStatus]}` : ''}
                  </span>
                  <span className="timeline__meta u-data">
                    {formatMoment(Date.parse(entry.createdAt))}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {job.deliveredAt || job.completedAt ? (
        <p className="detail__delivered" data-testid="delivered-at">
          <Check size={16} />
          <span>Delivered {formatTime(job.deliveredAt ?? job.completedAt)}</span>
        </p>
      ) : null}

      {/* Action bar ??? sticky, thumb-reachable */}
      <div className="actionbar" data-testid="job-actions">
        {action ? (
          <Button
            variant="primary"
            onClick={() => void (job.status === 'allocated' ? acceptAck() : transition(action.to))}
            loading={busy}
            data-testid={action.testid}
            className="actionbar__btn"
          >
            {job.status === 'allocated' ? <Check size={16} /> : <Play size={16} />}
            {action.label}
          </Button>
        ) : null}
        {canComplete ? (
          <Button
            variant="primary"
            onClick={() => setPodOpen(true)}
            data-testid="complete-job"
            className="actionbar__btn"
          >
            <Check size={16} />
            Complete delivery
          </Button>
        ) : null}
        {canException ? (
          <Button
            variant="secondary"
            onClick={() => setExceptionOpen(true)}
            data-testid="exception-job"
            className="actionbar__btn actionbar__btn--secondary"
          >
            <AlertTriangle size={16} />
            Report issue
          </Button>
        ) : null}
        {delivered ? (
          <p className="actionbar__done" data-testid="job-done-note">
            <Check size={16} /> This delivery is complete.
          </p>
        ) : null}
      </div>

      <PodSheet open={podOpen} onClose={() => setPodOpen(false)} job={job} />
      <ExceptionSheet open={exceptionOpen} onClose={() => setExceptionOpen(false)} job={job} />
    </article>
  );
}
